/** Shape returned by the API's presign endpoint; storage-provider agnostic. */
export interface Presigned {
  uploadUrl: string;
  method: 'PUT' | 'POST';
  headers: Record<string, string>;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  /** 0..1 */
  ratio: number;
}

/** Moves the bytes to storage. The default uses XMLHttpRequest for upload progress. */
export type UploadTransport = (
  presigned: Presigned,
  file: File,
  onProgress?: (p: UploadProgress) => void,
  signal?: AbortSignal,
) => Promise<void>;

export interface UploaderConfig<TPresign extends Presigned, TResult> {
  /** Ask the API for a presigned URL. */
  presign: (file: File) => Promise<TPresign>;
  /** Tell the API the bytes are in place. Returns the media record. */
  confirm: (presigned: TPresign, file: File) => Promise<TResult>;
  maxSizeBytes?: number;
  /** MIME types or extensions, e.g. ["image/jpeg", "image/png", ".webp"]. */
  accept?: readonly string[];
  /** Replace the byte transport, e.g. a multipart POST for another storage provider, or fetch in tests. */
  transport?: UploadTransport;
}

export type UploadErrorCode =
  'FILE_TOO_LARGE' | 'FILE_TYPE_NOT_ALLOWED' | 'UPLOAD_FAILED' | 'CANCELLED';

export class UploadError extends Error {
  override readonly name = 'UploadError';
  readonly code: UploadErrorCode;

  constructor(code: UploadErrorCode, message: string, cause?: unknown) {
    super(message, { cause });
    this.code = code;
  }
}

function isAccepted(file: File, accept: readonly string[] | undefined): boolean {
  if (!accept || accept.length === 0) return true;
  const name = file.name.toLowerCase();
  return accept.some((rule) => {
    const r = rule.toLowerCase();
    if (r.startsWith('.')) return name.endsWith(r);
    if (r.endsWith('/*')) return file.type.startsWith(r.slice(0, -1));
    return file.type === r;
  });
}

export const putWithProgress: UploadTransport = (presigned, file, onProgress, signal) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(presigned.method, presigned.uploadUrl, true);
    for (const [key, value] of Object.entries(presigned.headers)) xhr.setRequestHeader(key, value);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.({
          loaded: event.loaded,
          total: event.total,
          ratio: event.total ? event.loaded / event.total : 0,
        });
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new UploadError('UPLOAD_FAILED', `Storage responded with ${xhr.status}`));
    };
    xhr.onerror = () => reject(new UploadError('UPLOAD_FAILED', 'Network error during upload'));
    xhr.onabort = () => reject(new UploadError('CANCELLED', 'Upload canceled'));
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
};

export interface UploadOptions {
  onProgress?: (p: UploadProgress) => void;
  signal?: AbortSignal;
}

/**
 * Direct-to-storage upload: presign → PUT bytes with progress → confirm.
 * Lives outside the JSON axios path on purpose; storage does not speak our error envelope.
 */
export function createUploader<TPresign extends Presigned, TResult>(
  config: UploaderConfig<TPresign, TResult>,
) {
  const transport = config.transport ?? putWithProgress;

  function validate(file: File): UploadError | null {
    if (config.maxSizeBytes && file.size > config.maxSizeBytes) {
      return new UploadError('FILE_TOO_LARGE', `File exceeds ${config.maxSizeBytes} bytes`);
    }
    if (!isAccepted(file, config.accept)) {
      return new UploadError(
        'FILE_TYPE_NOT_ALLOWED',
        `Type ${file.type || file.name} is not allowed`,
      );
    }
    return null;
  }

  async function upload(file: File, options: UploadOptions = {}): Promise<TResult> {
    const invalid = validate(file);
    if (invalid) throw invalid;
    const presigned = await config.presign(file);
    await transport(presigned, file, options.onProgress, options.signal);
    return config.confirm(presigned, file);
  }

  return { validate, upload };
}

export type Uploader<TPresign extends Presigned, TResult> = ReturnType<
  typeof createUploader<TPresign, TResult>
>;
